import React from 'react';
import { Stack, Text } from '@solvhealth/jigsaw-v1';
import { formatPrice } from '~/core/util/string';
import { analyticsTrackEvent } from '~/core/analytics/index';
import { CDP_SELF_PAY_ACCORDION_CLICK } from '~/core/analytics/events';
import CDPServiceAccordion from '~/components/ClinicDetailPageFlexible/components/CDPServiceAccordion/CDPServiceAccordion';

type Props = {
  price?: string;
  minPrice?: string;
  maxPrice?: string;
  hasPriceRange: boolean;
  name: string;
  requiresBaseOfficeVisitFee?: boolean;
  acceptCash?: boolean;
  acceptInsurance?: boolean;
  walkinsAccepted?: boolean;
  notes?: string;
  baseOfficeVisit?: object;
  description?: string;
  bookText: string;
  useAssociatedTelemed?: boolean;
};

const getPriceText = (
  cashAccepted: boolean | undefined,
  acceptInsurance: boolean | undefined,
  price: string | undefined,
  minPrice: string | undefined,
  maxPrice: string | undefined,
  hasPriceRange: boolean,
  requiresBaseOfficeVisitFee: boolean | undefined,
  baseOfficeVisit: any
) => {
  if (!cashAccepted) return '';

  let baseOfficeVisitAddition = '';

  if (requiresBaseOfficeVisitFee && baseOfficeVisit) {
    let baseOfficeFee = '';
    if (baseOfficeVisit?.hasPriceRange && baseOfficeVisit.minPrice !== baseOfficeVisit.maxPrice) {
      baseOfficeFee = `${formatPrice(baseOfficeVisit.minPrice, 2, true, true)}-${formatPrice(
        baseOfficeVisit.maxPrice,
        2,
        true,
        true,
        false
      )}`;
    }
    if (baseOfficeVisit.price) {
      baseOfficeFee = `${formatPrice(baseOfficeVisit.price, 2, true, true)}`;
    }

    if (baseOfficeFee) {
      baseOfficeVisitAddition = ` (+ ${baseOfficeFee} office visit)`;
    }
  }

  if (hasPriceRange && minPrice !== maxPrice) {
    return `${formatPrice(minPrice, 2, true, true)}-${formatPrice(
      maxPrice,
      2,
      true,
      true,
      false
    )}${baseOfficeVisitAddition}`;
  }

  if (price) {
    return `${formatPrice(price, 2, true, true)}${baseOfficeVisitAddition}`;
  }

  return '';
};

const SelfPayAccordion = ({
  price,
  maxPrice,
  minPrice,
  hasPriceRange,
  name,
  requiresBaseOfficeVisitFee,
  acceptCash,
  acceptInsurance,
  baseOfficeVisit,
  notes,
  description,
}: Props) => {
  const selfPayPrice = getPriceText(
    acceptCash,
    acceptInsurance,
    price,
    minPrice,
    maxPrice,
    hasPriceRange,
    requiresBaseOfficeVisitFee,
    baseOfficeVisit
  );

  const handleAccordionClick = (service: string) => {
    analyticsTrackEvent(CDP_SELF_PAY_ACCORDION_CLICK, { action: service });
  };

  return (
    <CDPServiceAccordion
      body={
        <Stack space={2}>
          <Text variant="body2">{description}</Text>
          {notes && <Text variant="body2">{notes}</Text>}
        </Stack>
      }
      onClick={() => handleAccordionClick(name)}
      selfPayPrice={selfPayPrice}
      title={name}
    />
  );
};

export default SelfPayAccordion;
